/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Registrations Page - SuperAdmin Portal
 * ============================================================================
 *
 * Review and approve new organization registration requests.
 * Matches Phase-10 mockup design with data table layout.
 *
 * ============================================================================
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { ClipboardList, Check, X, RefreshCw, AlertCircle, MoreVertical, Eye, Building2, User, Mail, Phone, Briefcase, Calendar, Tag, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SortableHeader, useSort } from '@/components/ui/SortableHeader';
import { useRegistrations, useRegistrationStats, useApproveRegistration, useRejectRegistration } from '@/hooks/api/useRegistrations';
import { Registration, RegistrationStatus } from '@/types/superadmin.types';
import { useNotification } from '@/hooks/useNotification';
import { isApiError, formatErrorForDisplay } from '@/services/api/errors';
import { ConfirmationDialog } from '@/components/ui/ConfirmationDialog';

// Plan Badge Component
const PlanBadge: React.FC<{ tier: Registration['requestedPlan'] }> = ({ tier }) => {
  const styles = {
    STARTER: 'plan-badge starter',
    PROFESSIONAL: 'plan-badge professional',
    ENTERPRISE: 'plan-badge enterprise',
  };
  return (
    <span className={styles[tier]}>
      {tier.charAt(0) + tier.slice(1).toLowerCase()}
    </span>
  );
};

// Tab Component
const Tab: React.FC<{
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}> = ({ label, count, active, onClick }) => (
  <button
    onClick={onClick}
    className={`tab ${active ? 'active' : ''}`}
  >
    {label} ({count})
  </button>
);

// Mobile Action Menu Component
const MobileActionMenu: React.FC<{
  onApprove: () => void;
  onReject: () => void;
  isApproving: boolean;
  isRejecting: boolean;
  t: (key: string) => string;
}> = ({ onApprove, onReject, isApproving, isRejecting, t }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div className="mobile-action-menu" ref={menuRef}>
      <button
        className="mobile-action-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t('registrations.actions.label')}
      >
        <MoreVertical size={18} />
      </button>
      {isOpen && (
        <div className="mobile-action-dropdown">
          <button
            className="mobile-action-item approve"
            onClick={() => { onApprove(); setIsOpen(false); }}
            disabled={isApproving}
          >
            <Check size={16} />
            <span>{t('registrations.actions.approve')}</span>
          </button>
          <button
            className="mobile-action-item reject"
            onClick={() => { onReject(); setIsOpen(false); }}
            disabled={isRejecting}
          >
            <X size={16} />
            <span>{t('registrations.actions.reject')}</span>
          </button>
        </div>
      )}
    </div>
  );
};

// Skeleton Row Component
const SkeletonRow: React.FC = () => (
  <div className="data-table-row">
    <div className="cell-org">
      <div className="skeleton-box h-4 w-36 mb-2"></div>
      <div className="skeleton-box h-3 w-28"></div>
    </div>
    <div className="cell-contact">
      <div className="skeleton-box h-4 w-24 mb-2"></div>
      <div className="skeleton-box h-3 w-32"></div>
    </div>
    <div className="cell-plan">
      <div className="skeleton-box h-5 w-16 rounded"></div>
    </div>
    <div className="cell-submitted">
      <div className="skeleton-box h-4 w-20"></div>
    </div>
    <div className="cell-actions">
      <div className="skeleton-box h-8 w-20 rounded"></div>
      <div className="skeleton-box h-8 w-16 rounded"></div>
    </div>
  </div>
);

// Stat Card Skeleton
const StatCardSkeleton: React.FC = () => (
  <div className="stat-card">
    <div className="skeleton-box h-3 w-20 mb-2"></div>
    <div className="skeleton-box h-7 w-12"></div>
  </div>
);

// Dialog state type
interface DialogState {
  isOpen: boolean;
  registration: Registration | null;
}

// Registration Details Modal Component
const RegistrationDetailsModal: React.FC<{
  isOpen: boolean;
  registration: Registration | null;
  onClose: () => void;
  onApprove: (reg: Registration) => void;
  onReject: (reg: Registration) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: any;
}> = ({ isOpen, registration, onClose, onApprove, onReject, t }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setIsExiting(false);
    } else {
      setIsExiting(true);
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isVisible || !registration) return null;

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusConfig = (status: RegistrationStatus) => {
    switch (status) {
      case 'PENDING':
        return { color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.15)', label: t('registrations.tabs.pending') };
      case 'APPROVED':
        return { color: '#10B981', bg: 'rgba(16, 185, 129, 0.15)', label: t('registrations.status.approved') };
      case 'REJECTED':
        return { color: '#EF4444', bg: 'rgba(239, 68, 68, 0.15)', label: t('registrations.status.rejected') };
      default:
        return { color: '#94A3B8', bg: 'rgba(148, 163, 184, 0.15)', label: status };
    }
  };

  const statusConfig = getStatusConfig(registration.status);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`details-modal-backdrop ${isExiting ? 'exiting' : ''}`}
        onClick={onClose}
      />

      {/* Modal */}
      <div className={`details-modal ${isExiting ? 'exiting' : ''}`}>
        {/* Glow effect */}
        <div className="details-modal-glow" />

        {/* Header */}
        <div className="details-modal-header">
          <div className="details-modal-header-content">
            <div className="details-modal-icon">
              <Building2 size={24} />
            </div>
            <div>
              <h2 className="details-modal-title">{t('registrations.details.title')}</h2>
              <p className="details-modal-subtitle">{t('registrations.details.subtitle')}</p>
            </div>
          </div>
          <button className="details-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Status Badge */}
        <div className="details-modal-status-bar">
          <span
            className="details-status-badge"
            style={{ color: statusConfig.color, background: statusConfig.bg }}
          >
            {statusConfig.label}
          </span>
          <span className="details-submitted-date">
            <Calendar size={14} />
            {formatDate(registration.submittedAt)}
          </span>
        </div>

        {/* Content */}
        <div className="details-modal-content">
          {/* Organization Section */}
          <div className="details-section">
            <h3 className="details-section-title">
              <Building2 size={16} />
              {t('registrations.details.sections.organization')}
            </h3>
            <div className="details-grid">
              <div className="details-field">
                <span className="details-label">{t('registrations.details.fields.orgName')}</span>
                <span className="details-value primary">{registration.organizationName}</span>
              </div>
              <div className="details-field">
                <span className="details-label">{t('registrations.details.fields.legalName')}</span>
                <span className="details-value">{registration.legalName || '—'}</span>
              </div>
              <div className="details-field">
                <span className="details-label">{t('registrations.details.fields.taxId')}</span>
                <span className="details-value mono">{registration.taxId || '—'}</span>
              </div>
              <div className="details-field">
                <span className="details-label">{t('registrations.details.fields.industry')}</span>
                <span className="details-value">{registration.industry}</span>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="details-section">
            <h3 className="details-section-title">
              <User size={16} />
              {t('registrations.details.sections.contact')}
            </h3>
            <div className="details-grid">
              <div className="details-field">
                <span className="details-label">
                  <User size={12} />
                  {t('registrations.details.fields.contactName')}
                </span>
                <span className="details-value">{registration.contactName}</span>
              </div>
              <div className="details-field">
                <span className="details-label">
                  <Mail size={12} />
                  {t('registrations.details.fields.email')}
                </span>
                <span className="details-value">{registration.contactEmail}</span>
              </div>
              <div className="details-field full-width">
                <span className="details-label">
                  <Phone size={12} />
                  {t('registrations.details.fields.phone')}
                </span>
                <span className="details-value">{registration.contactPhone || '—'}</span>
              </div>
            </div>
          </div>

          {/* Plan & Referral Section */}
          <div className="details-section">
            <h3 className="details-section-title">
              <Briefcase size={16} />
              {t('registrations.details.sections.subscription')}
            </h3>
            <div className="details-grid">
              <div className="details-field">
                <span className="details-label">{t('registrations.details.fields.requestedPlan')}</span>
                <span className="details-value">
                  <PlanBadge tier={registration.requestedPlan} />
                </span>
              </div>
              <div className="details-field">
                <span className="details-label">
                  <Tag size={12} />
                  {t('registrations.details.fields.referralCode')}
                </span>
                <span className="details-value mono">{registration.referralCode || '—'}</span>
              </div>
            </div>
          </div>

          {/* Processing Info (if processed) */}
          {registration.processedAt && (
            <div className="details-section">
              <h3 className="details-section-title">
                <FileText size={16} />
                {t('registrations.details.sections.processing')}
              </h3>
              <div className="details-grid">
                <div className="details-field">
                  <span className="details-label">{t('registrations.details.fields.processedAt')}</span>
                  <span className="details-value">{formatDate(registration.processedAt)}</span>
                </div>
                <div className="details-field">
                  <span className="details-label">{t('registrations.details.fields.processedBy')}</span>
                  <span className="details-value">{registration.processedBy || '—'}</span>
                </div>
                {registration.notes && (
                  <div className="details-field full-width">
                    <span className="details-label">{t('registrations.details.fields.notes')}</span>
                    <span className="details-value notes">{registration.notes}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        {registration.status === 'PENDING' && (
          <div className="details-modal-actions">
            <button
              className="details-action-btn reject"
              onClick={() => { onClose(); onReject(registration); }}
            >
              <X size={16} />
              {t('registrations.actions.reject')}
            </button>
            <button
              className="details-action-btn approve"
              onClick={() => { onClose(); onApprove(registration); }}
            >
              <Check size={16} />
              {t('registrations.actions.approve')}
            </button>
          </div>
        )}

        {/* Corner decorations */}
        <div className="details-corner tl" />
        <div className="details-corner tr" />
        <div className="details-corner bl" />
        <div className="details-corner br" />
      </div>

      <style>{detailsModalStyles}</style>
    </>
  );
};

// Main Registrations Page
export default function RegistrationsPage() {
  const { t } = useTranslation('platform');
  const [filter, setFilter] = useState<RegistrationStatus | 'ALL'>('PENDING');
  const [currentPage, setCurrentPage] = useState(0);
  const { success, error: showError } = useNotification();

  // Sort state
  const { sortConfig, handleSort, getSortParam } = useSort('submittedAt', 'desc');

  // Dialog states
  const [approveDialog, setApproveDialog] = useState<DialogState>({ isOpen: false, registration: null });
  const [rejectDialog, setRejectDialog] = useState<DialogState>({ isOpen: false, registration: null });
  const [detailsModal, setDetailsModal] = useState<DialogState>({ isOpen: false, registration: null });

  // Use the registrations hook with filters
  const { data, isLoading, isFetching, error, refetch } = useRegistrations({
    page: currentPage,
    size: 20,
    status: filter !== 'ALL' ? filter : undefined,
    sort: getSortParam(),
  });

  // Use stats hook
  const { data: stats, isLoading: statsLoading } = useRegistrationStats();

  // Mutations
  const approveMutation = useApproveRegistration();
  const rejectMutation = useRejectRegistration();

  const registrations = data?.content || [];
  const totalElements = data?.page?.totalElements || 0;

  // Counts for tabs - use totalElements for the active filter to show accurate count
  const counts = useMemo(() => {
    const base = {
      PENDING: stats?.pendingCount || 0,
      APPROVED: stats?.approvedThisMonth || 0,
      REJECTED: stats?.rejectedThisMonth || 0,
    };

    // Override the count for the currently selected filter with accurate totalElements
    if (filter !== 'ALL' && totalElements !== undefined) {
      base[filter] = totalElements;
    }

    return base;
  }, [stats, filter, totalElements]);

  const formatTimeAgo = (date: string) => {
    const diff = Date.now() - new Date(date).getTime();
    const hours = Math.floor(diff / 3600000);
    if (hours < 1) return t('registrations.time.justNow');
    if (hours < 24) return t('registrations.time.hoursAgo', { count: hours });
    const days = Math.floor(hours / 24);
    if (days === 1) return t('registrations.time.yesterday');
    return t('registrations.time.daysAgo', { count: days });
  };

  // Open details modal
  const openDetailsModal = (registration: Registration) => {
    setDetailsModal({ isOpen: true, registration });
  };

  // Open approve dialog
  const openApproveDialog = (registration: Registration) => {
    setApproveDialog({ isOpen: true, registration });
  };

  // Open reject dialog
  const openRejectDialog = (registration: Registration) => {
    setRejectDialog({ isOpen: true, registration });
  };

  // Handle approve confirmation
  const handleApproveConfirm = () => {
    const reg = approveDialog.registration;
    if (!reg) return;

    approveMutation.mutate({ id: reg.id }, {
      onSuccess: () => {
        success(t('registrations.messages.approveSuccess', { name: reg.organizationName }));
        setApproveDialog({ isOpen: false, registration: null });
        refetch();
      },
      onError: (err) => {
        const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('registrations.messages.approveFailed') };
        showError(message);
        setApproveDialog({ isOpen: false, registration: null });
      },
    });
  };

  // Handle reject confirmation
  const handleRejectConfirm = (reason?: string) => {
    const reg = rejectDialog.registration;
    if (!reg || !reason) return;

    rejectMutation.mutate({ id: reg.id, data: { notes: reason } }, {
      onSuccess: () => {
        success(t('registrations.messages.rejectSuccess', { name: reg.organizationName }));
        setRejectDialog({ isOpen: false, registration: null });
        refetch();
      },
      onError: (err) => {
        const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('registrations.messages.rejectFailed') };
        showError(message);
        setRejectDialog({ isOpen: false, registration: null });
      },
    });
  };

  return (
    <div className="registrations-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('registrations.title')}</h1>
          <p className="page-subtitle">{t('registrations.subtitle')}</p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="refresh-btn"
          title={t('dashboard.refreshData')}
        >
          <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Stats Row */}
      <div className="stats-row">
        {statsLoading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : (
          <>
            <div className="stat-card">
              <div className="stat-label">{t('registrations.stats.pending')}</div>
              <div className="stat-value amber">{stats?.pendingCount || 0}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">{t('registrations.stats.approvedMonth')}</div>
              <div className="stat-value green">{stats?.approvedThisMonth || 0}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">{t('registrations.stats.rejectedMonth')}</div>
              <div className="stat-value red">{stats?.rejectedThisMonth || 0}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">{t('registrations.stats.totalProcessed')}</div>
              <div className="stat-value blue">{stats?.totalProcessed || 0}</div>
            </div>
          </>
        )}
      </div>

      {/* Tabs */}
      <div className="tabs">
        <Tab
          label={t('registrations.tabs.pending')}
          count={counts.PENDING}
          active={filter === 'PENDING'}
          onClick={() => setFilter('PENDING')}
        />
        <Tab
          label={t('registrations.tabs.approved')}
          count={counts.APPROVED}
          active={filter === 'APPROVED'}
          onClick={() => setFilter('APPROVED')}
        />
        <Tab
          label={t('registrations.tabs.rejected')}
          count={counts.REJECTED}
          active={filter === 'REJECTED'}
          onClick={() => setFilter('REJECTED')}
        />
      </div>

      {/* Data Table */}
      <div className="data-table">
        {/* Table Header */}
        <div className="data-table-header">
          <div className="data-table-th">
            <SortableHeader
              columnKey="orgName"
              label={t('registrations.table.organization')}
              sortConfig={sortConfig}
              onSort={handleSort}
            />
          </div>
          <div className="data-table-th">
            <SortableHeader
              columnKey="adminLastName"
              label={t('registrations.table.contact')}
              sortConfig={sortConfig}
              onSort={handleSort}
            />
          </div>
          <div className="data-table-th">
            <SortableHeader
              columnKey="selectedPlanId"
              label={t('registrations.table.plan')}
              sortConfig={sortConfig}
              onSort={handleSort}
            />
          </div>
          <div className="data-table-th">
            <SortableHeader
              columnKey="submittedAt"
              label={t('registrations.table.submitted')}
              sortConfig={sortConfig}
              onSort={handleSort}
            />
          </div>
          <div className="data-table-th">{t('registrations.table.actions')}</div>
        </div>

        {/* Table Body */}
        {isLoading ? (
          <>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </>
        ) : error ? (
          <div className="table-empty">
            <AlertCircle className="empty-icon text-red-500" />
            <p className="text-red-500">{t('registrations.error.loadFailed')}</p>
            <button
              onClick={() => refetch()}
              className="btn btn-sm btn-secondary mt-3"
            >
              <RefreshCw className="btn-icon" /> {t('common.tryAgain')}
            </button>
          </div>
        ) : registrations.length === 0 ? (
          <div className="table-empty">
            <ClipboardList className="empty-icon" />
            <p>{t('registrations.empty', { status: t(`registrations.tabs.${filter.toLowerCase()}`).toLowerCase() })}</p>
          </div>
        ) : (
          registrations.map((reg) => (
            <div key={reg.id} className="data-table-row" onClick={() => openDetailsModal(reg)}>
              <div className="cell-org">
                <div className="org-name">{reg.organizationName}</div>
                <div className="org-nif">NIF: {reg.taxId || 'N/A'}</div>
              </div>
              <div className="cell-contact">
                <div className="contact-name">{reg.contactName}</div>
                <div className="contact-email">{reg.contactEmail}</div>
              </div>
              <div className="cell-plan">
                <PlanBadge tier={reg.requestedPlan} />
              </div>
              <div className="cell-submitted">
                {formatTimeAgo(reg.submittedAt)}
              </div>
              <div className="cell-actions" onClick={(e) => e.stopPropagation()}>
                {reg.status === 'PENDING' && (
                  <>
                    {/* Desktop buttons */}
                    <div className="desktop-actions">
                      <button
                        className="btn btn-sm btn-ghost"
                        onClick={() => openDetailsModal(reg)}
                        title={t('registrations.actions.viewDetails')}
                      >
                        <Eye className="btn-icon" />
                      </button>
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => openApproveDialog(reg)}
                        disabled={approveMutation.isPending}
                      >
                        <Check className="btn-icon" /> {t('registrations.actions.approve')}
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => openRejectDialog(reg)}
                        disabled={rejectMutation.isPending}
                      >
                        {t('registrations.actions.reject')}
                      </button>
                    </div>
                    {/* Mobile action menu */}
                    <div className="mobile-actions">
                      <MobileActionMenu
                        onApprove={() => openApproveDialog(reg)}
                        onReject={() => openRejectDialog(reg)}
                        isApproving={approveMutation.isPending}
                        isRejecting={rejectMutation.isPending}
                        t={t}
                      />
                    </div>
                  </>
                )}
                {reg.status === 'APPROVED' && (
                  <div className="status-actions">
                    <button
                      className="btn btn-sm btn-ghost"
                      onClick={() => openDetailsModal(reg)}
                      title={t('registrations.actions.viewDetails')}
                    >
                      <Eye className="btn-icon" />
                    </button>
                    <span className="status-text green">{t('registrations.status.approved')}</span>
                  </div>
                )}
                {reg.status === 'REJECTED' && (
                  <div className="status-actions">
                    <button
                      className="btn btn-sm btn-ghost"
                      onClick={() => openDetailsModal(reg)}
                      title={t('registrations.actions.viewDetails')}
                    >
                      <Eye className="btn-icon" />
                    </button>
                    <span className="status-text red">{t('registrations.status.rejected')}</span>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Results count */}
      {!isLoading && totalElements > 0 && (
        <div className="results-count">
          {t('registrations.resultsCount', { showing: registrations.length, total: totalElements })}
        </div>
      )}

      {/* Approve Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={approveDialog.isOpen}
        variant="approve"
        title={t('registrations.dialogs.approve.title')}
        message={t('registrations.dialogs.approve.message')}
        details={approveDialog.registration && (
          <div className="dialog-org-details">
            <div className="detail-row">
              <span className="detail-label">{t('registrations.dialogs.labels.organization')}:</span>
              <span className="detail-value">{approveDialog.registration.organizationName}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">{t('registrations.dialogs.labels.contact')}:</span>
              <span className="detail-value">{approveDialog.registration.contactName}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">{t('registrations.dialogs.labels.plan')}:</span>
              <span className="detail-value">{approveDialog.registration.requestedPlan}</span>
            </div>
            <div className="trial-notice">
              <span>{t('registrations.dialogs.approve.trialNotice')}</span>
            </div>
          </div>
        )}
        confirmText={t('registrations.dialogs.approve.confirmText')}
        isLoading={approveMutation.isPending}
        onConfirm={handleApproveConfirm}
        onCancel={() => setApproveDialog({ isOpen: false, registration: null })}
      />

      {/* Reject Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={rejectDialog.isOpen}
        variant="reject"
        title={t('registrations.dialogs.reject.title')}
        message={t('registrations.dialogs.reject.message')}
        details={rejectDialog.registration && (
          <div className="dialog-org-details">
            <div className="detail-row">
              <span className="detail-label">{t('registrations.dialogs.labels.organization')}:</span>
              <span className="detail-value">{rejectDialog.registration.organizationName}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">{t('registrations.dialogs.labels.contact')}:</span>
              <span className="detail-value">{rejectDialog.registration.contactEmail}</span>
            </div>
          </div>
        )}
        showInput
        inputLabel={t('registrations.dialogs.reject.inputLabel')}
        inputPlaceholder={t('registrations.dialogs.reject.inputPlaceholder')}
        inputRequired
        confirmText={t('registrations.dialogs.reject.confirmText')}
        isLoading={rejectMutation.isPending}
        onConfirm={handleRejectConfirm}
        onCancel={() => setRejectDialog({ isOpen: false, registration: null })}
      />

      {/* Registration Details Modal */}
      <RegistrationDetailsModal
        isOpen={detailsModal.isOpen}
        registration={detailsModal.registration}
        onClose={() => setDetailsModal({ isOpen: false, registration: null })}
        onApprove={openApproveDialog}
        onReject={openRejectDialog}
        t={t}
      />

      <style>{registrationsStyles}</style>
    </div>
  );
}

// ============================================================================
// STYLES - Matching Phase-10 Mockup
// ============================================================================

const registrationsStyles = `
  .registrations-page {
    /* Container styles */
  }

  /* Page Header */
  .page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
  }

  .page-title {
    font-family: var(--font-heading);
    font-size: 22px;
    font-weight: 700;
    color: var(--color-text-primary);
  }

  .page-subtitle {
    font-size: 13px;
    color: var(--color-text-secondary);
  }

  .refresh-btn {
    padding: 8px;
    border-radius: 8px;
    border: 1px solid var(--color-border-default);
    background: transparent;
    color: var(--color-text-secondary);
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .refresh-btn:hover {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
  }

  .refresh-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Stats Row */
  .stats-row {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin-bottom: 16px;
  }

  .stat-card {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 10px;
    padding: 12px 16px;
  }

  .stat-label {
    font-size: 11px;
    font-weight: 500;
    color: var(--color-text-muted);
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .stat-value {
    font-family: var(--font-mono);
    font-size: 24px;
    font-weight: 700;
    color: var(--color-text-primary);
  }

  .stat-value.amber { color: #F59E0B; }
  .stat-value.green { color: #10B981; }
  .stat-value.red { color: #EF4444; }
  .stat-value.blue { color: #3B82F6; }

  /* Tabs */
  .tabs {
    display: flex;
    gap: 2px;
    background: var(--color-bg-primary);
    padding: 3px;
    border-radius: 8px;
    margin-bottom: 16px;
    width: fit-content;
  }

  .tab {
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 500;
    border-radius: 6px;
    border: none;
    background: transparent;
    color: var(--color-text-secondary);
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .tab:hover {
    color: var(--color-text-primary);
    background: var(--color-bg-hover);
  }

  .tab.active {
    background: var(--color-brand-primary);
    color: white;
  }

  /* Data Table */
  .data-table {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 12px;
    overflow: hidden;
  }

  .data-table-header {
    display: grid;
    grid-template-columns: 2fr 1.5fr 1fr 1fr 150px;
    padding: 12px 16px;
    background: var(--color-bg-primary);
    border-bottom: 1px solid var(--color-border-subtle);
  }

  .data-table-th {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--color-text-muted);
  }

  .data-table-row {
    display: grid;
    grid-template-columns: 2fr 1.5fr 1fr 1fr 150px;
    padding: 12px 16px;
    align-items: center;
    border-bottom: 1px solid var(--color-border-subtle);
    cursor: default;
  }

  .data-table-row:last-child {
    border-bottom: none;
  }

  .data-table-row:hover {
    background: var(--color-bg-hover);
  }

  /* Cell Styles */
  .cell-org .org-name {
    font-weight: 500;
    color: var(--color-text-primary);
    font-size: 13px;
  }

  .cell-org .org-nif {
    font-size: 11px;
    color: var(--color-text-secondary);
  }

  .cell-contact .contact-name {
    color: var(--color-text-primary);
    font-size: 13px;
  }

  .cell-contact .contact-email {
    font-size: 11px;
    color: var(--color-text-secondary);
  }

  .cell-submitted {
    font-size: 12px;
    color: var(--color-text-secondary);
  }

  .cell-actions {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
  }

  .desktop-actions {
    display: flex;
    gap: 8px;
  }

  .mobile-actions {
    display: none;
  }

  .status-text {
    font-size: 12px;
    font-weight: 500;
  }

  .status-text.green { color: #10B981; }
  .status-text.red { color: #EF4444; }

  /* Mobile Action Menu */
  .mobile-action-menu {
    position: relative;
  }

  .mobile-action-trigger {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    border: 1px solid var(--color-border-default);
    background: var(--color-bg-primary);
    color: var(--color-text-secondary);
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .mobile-action-trigger:hover {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
  }

  .mobile-action-dropdown {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 4px;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-default);
    border-radius: 10px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
    overflow: hidden;
    z-index: 100;
    min-width: 140px;
    animation: dropdownFadeIn 0.15s ease;
  }

  @keyframes dropdownFadeIn {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .mobile-action-item {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 12px 16px;
    border: none;
    background: transparent;
    color: var(--color-text-primary);
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s ease;
    text-align: left;
  }

  .mobile-action-item:hover:not(:disabled) {
    background: var(--color-bg-hover);
  }

  .mobile-action-item:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .mobile-action-item.approve {
    color: #10B981;
  }

  .mobile-action-item.approve:hover:not(:disabled) {
    background: rgba(16, 185, 129, 0.1);
  }

  .mobile-action-item.reject {
    color: #EF4444;
  }

  .mobile-action-item.reject:hover:not(:disabled) {
    background: rgba(239, 68, 68, 0.1);
  }

  .mobile-action-item + .mobile-action-item {
    border-top: 1px solid var(--color-border-subtle);
  }

  /* Plan Badges */
  .plan-badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
  }

  .plan-badge.starter {
    background: rgba(100, 116, 139, 0.15);
    color: #94A3B8;
  }

  .plan-badge.pro {
    background: rgba(139, 92, 246, 0.15);
    color: #8B5CF6;
  }

  .plan-badge.enterprise {
    background: rgba(139, 0, 0, 0.15);
    color: var(--color-brand-primary);
  }

  /* Buttons */
  .btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 500;
    border-radius: 6px;
    border: none;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .btn-icon {
    width: 14px;
    height: 14px;
  }

  .btn-sm {
    padding: 6px 12px;
    font-size: 12px;
  }

  .btn-success {
    background: var(--color-success);
    color: white;
  }

  .btn-success:hover:not(:disabled) {
    opacity: 0.9;
  }

  .btn-secondary {
    background: var(--color-bg-primary);
    color: var(--color-text-primary);
    border: 1px solid var(--color-border-default);
  }

  .btn-secondary:hover:not(:disabled) {
    background: var(--color-bg-hover);
  }

  /* Skeleton */
  .skeleton-box {
    background: var(--color-bg-primary);
    border-radius: 4px;
    animation: pulse 1.5s ease-in-out infinite;
  }

  @keyframes pulse {
    0%, 100% { opacity: 0.4; }
    50% { opacity: 0.7; }
  }

  /* Loading & Empty States */
  .table-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px 16px;
  }

  .spinner {
    width: 24px;
    height: 24px;
    border: 2px solid var(--color-border-default);
    border-top-color: var(--color-brand-primary);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .table-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 16px;
    color: var(--color-text-muted);
  }

  .empty-icon {
    width: 48px;
    height: 48px;
    margin-bottom: 12px;
    opacity: 0.5;
  }

  .table-empty p {
    font-size: 13px;
  }

  .results-count {
    margin-top: 12px;
    font-size: 13px;
    color: var(--color-text-muted);
  }

  /* Responsive */
  @media (max-width: 1024px) {
    .stats-row {
      grid-template-columns: repeat(2, 1fr);
    }

    .data-table-header,
    .data-table-row {
      grid-template-columns: 1.5fr 1.5fr 1fr 120px;
    }

    .cell-submitted {
      display: none;
    }

    .data-table-th:nth-child(4) {
      display: none;
    }
  }

  @media (max-width: 768px) {
    .stats-row {
      grid-template-columns: repeat(2, 1fr);
    }

    .data-table-header,
    .data-table-row {
      grid-template-columns: 1fr 1fr 50px;
    }

    .cell-plan {
      display: none;
    }

    .data-table-th:nth-child(3) {
      display: none;
    }

    /* Switch to mobile action menu */
    .desktop-actions {
      display: none;
    }

    .mobile-actions {
      display: block;
    }

    /* Make tabs scrollable */
    .tabs {
      overflow-x: auto;
      width: 100%;
      -webkit-overflow-scrolling: touch;
    }

    .tab {
      white-space: nowrap;
      padding: 8px 12px;
      font-size: 12px;
    }
  }

  @media (max-width: 480px) {
    .page-title {
      font-size: 18px;
    }

    .page-subtitle {
      font-size: 12px;
    }

    .stats-row {
      grid-template-columns: 1fr 1fr;
      gap: 8px;
    }

    .stat-card {
      padding: 10px 12px;
    }

    .stat-value {
      font-size: 20px;
    }

    .stat-label {
      font-size: 10px;
    }

    /* Card-like layout for very small screens */
    .data-table-header {
      display: none;
    }

    .data-table-row {
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding: 16px;
      border-bottom: 1px solid var(--color-border-subtle);
      position: relative;
    }

    .data-table-row:last-child {
      border-bottom: none;
    }

    .cell-org,
    .cell-contact {
      width: 100%;
    }

    .cell-org .org-name {
      font-size: 14px;
      font-weight: 600;
    }

    .cell-contact {
      padding-top: 4px;
      border-top: 1px dashed var(--color-border-subtle);
    }

    .cell-actions {
      position: absolute;
      top: 12px;
      right: 12px;
    }

    .mobile-action-dropdown {
      right: 0;
    }
  }

  /* Dialog Details Styling */
  .dialog-org-details {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .dialog-org-details .detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;
  }

  .dialog-org-details .detail-label {
    font-size: 12px;
    color: var(--color-text-muted);
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .dialog-org-details .detail-value {
    font-size: 13px;
    font-weight: 600;
    color: var(--color-text-primary);
  }

  .dialog-org-details .trial-notice {
    margin-top: 8px;
    padding: 10px 12px;
    background: rgba(16, 185, 129, 0.1);
    border: 1px solid rgba(16, 185, 129, 0.2);
    border-radius: 8px;
    font-size: 12px;
    color: #10B981;
    text-align: center;
  }

  /* Ghost Button */
  .btn-ghost {
    background: transparent;
    color: var(--color-text-secondary);
    border: 1px solid transparent;
    padding: 6px 8px;
  }

  .btn-ghost:hover:not(:disabled) {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
    border-color: var(--color-border-default);
  }

  /* Status Actions Container */
  .status-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  /* Clickable Row */
  .data-table-row {
    cursor: pointer;
  }
`;

// Details Modal Styles
const detailsModalStyles = `
  /* Backdrop */
  .details-modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.75);
    backdrop-filter: blur(12px);
    z-index: 9998;
    animation: detailsBackdropIn 0.3s ease-out;
  }

  .details-modal-backdrop.exiting {
    animation: detailsBackdropOut 0.3s ease-out forwards;
  }

  @keyframes detailsBackdropIn {
    from { opacity: 0; backdrop-filter: blur(0); }
    to { opacity: 1; backdrop-filter: blur(12px); }
  }

  @keyframes detailsBackdropOut {
    from { opacity: 1; }
    to { opacity: 0; }
  }

  /* Modal */
  .details-modal {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90%;
    max-width: 560px;
    max-height: 85vh;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-default);
    border-radius: 20px;
    z-index: 9999;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    animation: detailsModalIn 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .details-modal.exiting {
    animation: detailsModalOut 0.3s ease-in forwards;
  }

  @keyframes detailsModalIn {
    from {
      opacity: 0;
      transform: translate(-50%, -48%) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
  }

  @keyframes detailsModalOut {
    from {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
    to {
      opacity: 0;
      transform: translate(-50%, -52%) scale(0.95);
    }
  }

  /* Glow Effect */
  .details-modal-glow {
    position: absolute;
    inset: -2px;
    border-radius: 22px;
    z-index: -1;
    background: linear-gradient(135deg, var(--color-brand-primary), rgba(139, 0, 0, 0.3), var(--color-brand-primary));
    opacity: 0.5;
    animation: detailsGlowPulse 4s ease-in-out infinite;
  }

  @keyframes detailsGlowPulse {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 0.6; }
  }

  /* Header */
  .details-modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px;
    border-bottom: 1px solid var(--color-border-subtle);
    background: linear-gradient(180deg, var(--color-bg-primary) 0%, transparent 100%);
  }

  .details-modal-header-content {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .details-modal-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, rgba(139, 0, 0, 0.2), rgba(139, 0, 0, 0.1));
    border: 1px solid rgba(139, 0, 0, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-brand-primary);
  }

  .details-modal-title {
    font-family: var(--font-heading);
    font-size: 18px;
    font-weight: 700;
    color: var(--color-text-primary);
    margin: 0;
  }

  .details-modal-subtitle {
    font-size: 12px;
    color: var(--color-text-muted);
    margin: 2px 0 0;
  }

  .details-modal-close {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    border: 1px solid var(--color-border-default);
    background: var(--color-bg-primary);
    color: var(--color-text-secondary);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .details-modal-close:hover {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
    border-color: var(--color-brand-primary);
  }

  /* Status Bar */
  .details-modal-status-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 24px;
    background: var(--color-bg-primary);
    border-bottom: 1px solid var(--color-border-subtle);
  }

  .details-status-badge {
    display: inline-flex;
    align-items: center;
    padding: 4px 12px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .details-submitted-date {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: var(--color-text-muted);
  }

  /* Content */
  .details-modal-content {
    flex: 1;
    overflow-y: auto;
    padding: 20px 24px;
  }

  /* Sections */
  .details-section {
    margin-bottom: 24px;
  }

  .details-section:last-child {
    margin-bottom: 0;
  }

  .details-section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--color-text-muted);
    margin: 0 0 12px;
    padding-bottom: 8px;
    border-bottom: 1px dashed var(--color-border-subtle);
  }

  .details-section-title svg {
    opacity: 0.7;
  }

  /* Grid */
  .details-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
  }

  .details-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .details-field.full-width {
    grid-column: 1 / -1;
  }

  .details-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 10px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--color-text-muted);
  }

  .details-label svg {
    opacity: 0.6;
  }

  .details-value {
    font-size: 14px;
    color: var(--color-text-primary);
    word-break: break-word;
  }

  .details-value.primary {
    font-weight: 600;
    font-size: 15px;
  }

  .details-value.mono {
    font-family: var(--font-mono);
    font-size: 13px;
  }

  .details-value.notes {
    font-size: 13px;
    line-height: 1.5;
    padding: 10px 12px;
    background: var(--color-bg-primary);
    border-radius: 8px;
    border: 1px solid var(--color-border-subtle);
  }

  /* Actions */
  .details-modal-actions {
    display: flex;
    gap: 12px;
    padding: 16px 24px;
    background: var(--color-bg-primary);
    border-top: 1px solid var(--color-border-subtle);
  }

  .details-action-btn {
    flex: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 20px;
    font-size: 14px;
    font-weight: 600;
    border-radius: 10px;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .details-action-btn.approve {
    background: linear-gradient(135deg, #10B981, #059669);
    color: white;
    box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
  }

  .details-action-btn.approve:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
  }

  .details-action-btn.reject {
    background: var(--color-bg-secondary);
    color: var(--color-text-secondary);
    border: 1px solid var(--color-border-default);
  }

  .details-action-btn.reject:hover {
    background: rgba(239, 68, 68, 0.1);
    color: #EF4444;
    border-color: rgba(239, 68, 68, 0.3);
  }

  /* Corner Decorations */
  .details-corner {
    position: absolute;
    width: 16px;
    height: 16px;
    border-color: var(--color-border-default);
    opacity: 0.4;
    pointer-events: none;
  }

  .details-corner.tl {
    top: 6px;
    left: 6px;
    border-left: 2px solid;
    border-top: 2px solid;
    border-top-left-radius: 4px;
  }

  .details-corner.tr {
    top: 6px;
    right: 6px;
    border-right: 2px solid;
    border-top: 2px solid;
    border-top-right-radius: 4px;
  }

  .details-corner.bl {
    bottom: 6px;
    left: 6px;
    border-left: 2px solid;
    border-bottom: 2px solid;
    border-bottom-left-radius: 4px;
  }

  .details-corner.br {
    bottom: 6px;
    right: 6px;
    border-right: 2px solid;
    border-bottom: 2px solid;
    border-bottom-right-radius: 4px;
  }

  /* Mobile Responsive */
  @media (max-width: 560px) {
    .details-modal {
      width: 95%;
      max-height: 90vh;
      border-radius: 16px;
    }

    .details-modal-header {
      padding: 16px 20px;
    }

    .details-modal-icon {
      width: 40px;
      height: 40px;
    }

    .details-modal-title {
      font-size: 16px;
    }

    .details-modal-status-bar {
      padding: 10px 20px;
      flex-direction: column;
      gap: 8px;
      align-items: flex-start;
    }

    .details-modal-content {
      padding: 16px 20px;
    }

    .details-grid {
      grid-template-columns: 1fr;
    }

    .details-modal-actions {
      padding: 14px 20px;
      flex-direction: column-reverse;
    }

    .details-action-btn {
      width: 100%;
    }
  }

  /* Light Theme Adjustments */
  [data-theme="light"] .details-modal-backdrop {
    background: rgba(0, 0, 0, 0.5);
  }

  [data-theme="light"] .details-modal-glow {
    opacity: 0.3;
  }
`;
